import { useContext, useEffect, useRef } from 'react';
import reciteMeService from '@connectedhomes/nucleus/components/nsx-header/reciteme';

import { useSections } from 'components/common/nsx-header/hooks/useSections';
import { usePartner } from 'components/app/routes/usePartner';
import { useRevampJourney } from 'hooks/useRevampJourney';
import { ResponsiveContext } from 'components/app/AppContext';
import { loadScript } from 'utils/misc';

type Route = {
  name?: string;
};

type NsxHeaderProps = {
  route?: Route;
};

const { VITE_RECITE_ME_URL, VITE_RECITE_ME_KEY } = import.meta.env;

export const NsxHeader = ({ route }: NsxHeaderProps) => {
  const headerRef = useRef<HTMLElement>(null);
  const { partnerLogo } = usePartner();
  const {
    sections,
    menuBanner,
    secondaryBanner,
    nsxHeaderQuickLinks,
    primary,
    secondary,
    hideA11yBtn,
    nsHeaderQuickLinks
  } = useSections();
  const { isDesktop } = useContext(ResponsiveContext);

  const { isRevampJourney } = useRevampJourney();
  const headerElem = headerRef?.current;
  const accessibilityBtn = headerElem?.shadowRoot?.querySelector(
    '.accessibility-tools'
  ) as HTMLButtonElement;

  const handleAccessibilityClick = async () => {
    await loadScript(`${VITE_RECITE_ME_URL}${VITE_RECITE_ME_KEY}`);
    if (window.Recite) {
      reciteMeService();
    }
  };

  useEffect(() => {
    if (!!hideA11yBtn || !isRevampJourney) {
      return;
    }

    accessibilityBtn?.removeEventListener('click', handleAccessibilityClick);
    accessibilityBtn?.addEventListener('click', handleAccessibilityClick);

    return () => {
      accessibilityBtn?.removeEventListener('click', handleAccessibilityClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessibilityBtn]);

  if (isRevampJourney) {
    return (
      <ns-header
        ref={headerRef}
        primary={[]}
        secondary={
          [
            { text: 'Search', href: 'https://www.britishgas.co.uk/search' },
            {
              text: 'Accessibility toolbar',
              type: 'button',
              classes: 'accessibility-tools'
            }
          ] as object[]
        }
        hide-links={!isDesktop}
        secondary-banner={JSON.stringify([
          {
            text: `Switch to an energy provider trusted by millions.`
          }
        ])}
      />
    );
  }

  return partnerLogo ? (
    <ns-header
      logo={partnerLogo}
      alt={route?.name}
      primary={primary}
      secondary={secondary}
      quick-links={JSON.stringify(nsHeaderQuickLinks)}
      secondary-banner={JSON.stringify(secondaryBanner)}
      menu-banner={menuBanner}
    ></ns-header>
  ) : (
    <nsx-header
      menu-banner={menuBanner}
      secondary-banner={JSON.stringify(secondaryBanner)}
      quick-links={JSON.stringify(nsxHeaderQuickLinks)}
      sections={sections}
      hide-a11y-btn={hideA11yBtn}
    ></nsx-header>
  );
};
